<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import LogoUpload from './LogoUpload.vue';

const props = defineProps({
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const state = ref({
  name: '',
  description: '',
  isPublished: false, // false = draft, true = published
  logoFile: null,
});

// LogoUpload 组件引用
const logoUploadRef = ref(null);

// 表单验证错误
const errors = ref({
  name: '',
});

// 提交按钮文本
const submitLabel = computed(() => {
  return props.isEdit
    ? t('PILOT.KNOWLEDGE_BASES.FORM.UPDATE')
    : t('PILOT.KNOWLEDGE_BASES.FORM.SUBMIT');
});

const hasErrors = computed(() => {
  return Object.values(errors.value).some(error => error !== '');
});

const handleCancel = () => emit('cancel');

const validateForm = () => {
  errors.value = {
    name: '',
  };

  if (!state.value.name || state.value.name.trim() === '') {
    errors.value.name = t('PILOT.KNOWLEDGE_BASES.FORM.NAME.ERROR');
    return false;
  }

  return true;
};

const prepareKnowledgeBaseDetails = () => {
  const formData = new FormData();
  formData.append('knowledge_base[name]', state.value.name);
  formData.append('knowledge_base[description]', state.value.description);
  formData.append(
    'knowledge_base[status]',
    state.value.isPublished ? 'published' : 'draft'
  );
  if (state.value.logoFile) {
    formData.append('knowledge_base[logo]', state.value.logoFile);
  }

  return formData;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  emit('submit', prepareKnowledgeBaseDetails());
};

const resetForm = () => {
  state.value = {
    name: '',
    description: '',
    isPublished: false,
    logoFile: null,
    logoUrl: '', // 重置 logo URL
  };
  errors.value = {
    name: '',
  };
  // 重置 LogoUpload 组件状态
  logoUploadRef.value?.reset();
};

// 设置表单数据（用于编辑）
const setFormData = knowledgeBase => {
  state.value = {
    name: knowledgeBase.name || '',
    description: knowledgeBase.description || '',
    isPublished: knowledgeBase.status === 'published',
    logoFile: null,
  };
  // 如果有 logo，需要在 LogoUpload 组件中显示
  if (knowledgeBase.logo_url) {
    state.value.logoUrl = knowledgeBase.logo_url;
  }
};

defineExpose({ resetForm, setFormData });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Logo 上传 -->
    <LogoUpload
      ref="logoUploadRef"
      v-model="state.logoFile"
      :label="t('PILOT.KNOWLEDGE_BASES.FORM.LOGO.LABEL')"
      :existing-logo-url="state.logoUrl"
    />

    <Input
      v-model="state.name"
      :label="t('PILOT.KNOWLEDGE_BASES.FORM.NAME.LABEL')"
      :placeholder="t('PILOT.KNOWLEDGE_BASES.FORM.NAME.PLACEHOLDER')"
      :message="errors.name"
      :message-type="errors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.description"
      type="textarea"
      :label="t('PILOT.KNOWLEDGE_BASES.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('PILOT.KNOWLEDGE_BASES.FORM.DESCRIPTION.PLACEHOLDER')"
      :rows="4"
    />

    <!-- 状态开关 -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm text-n-slate-12 dark:text-n-slate-1 font-medium">
        {{ t('PILOT.KNOWLEDGE_BASES.FORM.STATUS.LABEL') }}
      </span>
      <div class="flex items-center gap-3">
        <span
          class="text-sm"
          :class="
            state.isPublished
              ? 'text-n-slate-9 dark:text-n-slate-6'
              : 'text-n-slate-12 dark:text-n-slate-1 font-medium'
          "
        >
          {{ t('PILOT.KNOWLEDGE_BASES.STATUS.DRAFT') }}
        </span>
        <Switch v-model="state.isPublished" />
        <span
          class="text-sm"
          :class="
            state.isPublished
              ? 'text-n-slate-12 dark:text-n-slate-1 font-medium'
              : 'text-n-slate-9 dark:text-n-slate-6'
          "
        >
          {{ t('PILOT.KNOWLEDGE_BASES.STATUS.PUBLISHED') }}
        </span>
      </div>
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('PILOT.KNOWLEDGE_BASES.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button type="submit" :label="submitLabel" class="w-full" />
    </div>
  </form>
</template>
